import { ErrorView1 } from '@epihub/web/src/components/ErrorView1';
import { TextGenerateEffect, type WordItem } from '@epihub/web/src/tailwind/components/TextGenerateEffect';
import React from 'react';
import { HoverBorderGradient } from '@epihub/web/src/tailwind/components/Buttons/HoverBorderGradient';
import { IconArrowLeft, IconFileDownload } from '@tabler/icons-react';
import { navigateTo } from '@epihub/web/src/routes/nav';
import { homeChatRoute } from '@krishanmadan/core/src/routes/frontendRoutes';
import { styled, useTheme } from '@krishanmadan/frontend/src/theme/theme';
import { useGlobalStore } from '@krishanmadan/frontend/src/store/useGlobalStore';
import { downloadCVProgrammatically } from '@krishanmadan/frontend/src/lib/downloadCVProgrammatically';

const BaseText = ({ children }) => <span>{children}</span>;
const Heading = ({ children }) => {
  const theme = useTheme();
  return <h1 className='text-5xl font-bold' style={{ marginTop: 24, color: theme.v0.col.warning.hex }}>{children}</h1>;
};

const cvDownloadStories: Record<string, Record<'it' | 'en', WordItem[]>> = {
  developer: {
    en: [
      { text: 'Why did the developer keep clicking refresh?', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Because the CV download got lost in the void:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click below to try again', Wrapper: Heading },
    ],
    it: [
      { text: 'Perché lo sviluppatore continuava a cliccare aggiorna?', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Perché il download del CV si è perso nel vuoto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca sotto per riprovare', Wrapper: Heading },
    ],
  },
  wizard: {
    en: [
      { text: 'A wizard cast a spell to summon the CV. The scroll remained blank.', Wrapper: BaseText },
      { ms: 800 },
      { text: 'Perhaps a manual incantation is needed:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click the button below', Wrapper: Heading },
    ],
    it: [
      { text: 'Un mago lanciò un incantesimo per evocare il CV. La pergamena rimase vuota.', Wrapper: BaseText },
      { ms: 800 },
      { text: "Forse serve un'incantesimo manuale:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca il pulsante sotto', Wrapper: Heading },
    ],
  },
  babel: {
    en: [
      {
        text: 'The Library of Babel contains every CV ever written. Yours got lost in the infinite shelves.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'The librarian suggests:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Download it manually', Wrapper: Heading },
    ],
    it: [
      {
        text: 'La Biblioteca di Babele contiene ogni CV mai scritto. Il tuo si è perso negli scaffali infiniti.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Il bibliotecario suggerisce:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Scaricalo manualmente', Wrapper: Heading },
    ],
  },
  philosopher: {
    en: [
      {
        text: 'A philosopher asked: "If a CV downloads and no one sees it, did it really download?"',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'The answer:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click below to be sure', Wrapper: Heading },
    ],
    it: [
      {
        text: 'Un filosofo chiese: "Se un CV si scarica e nessuno lo vede, si è davvero scaricato?"',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'La risposta:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca sotto per esserne certo', Wrapper: Heading },
    ],
  },
  schrodinger: {
    en: [
      { text: "Schrödinger's CV: both downloaded and not downloaded until you check your folder.", Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Collapse the wavefunction below', Wrapper: Heading },
    ],
    it: [
      { text: 'Il CV di Schrödinger: scaricato e non scaricato finché non controlli la cartella.', Wrapper: BaseText },
      { ms: 1200 },
      { text: "Collassa la funzione d'onda qui sotto", Wrapper: Heading },
    ],
  },
  transatlantic: {
    en: [
      {
        text: 'Europe tried to download the CV but the transatlantic cable got tangled.',
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Tariff-free download available:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click the button below', Wrapper: Heading },
    ],
    it: [
      {
        text: "L'Europa provò a scaricare il CV ma il cavo transatlantico si è aggrovigliato.",
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Download senza dazi disponibile:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca il pulsante sotto', Wrapper: Heading },
    ],
  },
  brexit: {
    en: [
      { text: 'Britain left the EU and the CV got stuck in customs.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Skip the bureaucracy:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Download directly below', Wrapper: Heading },
    ],
    it: [
      {
        text: "La Gran Bretagna lasciò l'UE e il CV rimase bloccato in dogana.",
        Wrapper: BaseText,
      },
      { ms: 1000 },
      { text: 'Salta la burocrazia:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Scarica direttamente qui sotto', Wrapper: Heading },
    ],
  },
  climate: {
    en: [
      { text: 'The CV tried to download but the server was powered by coal.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'For a greener alternative:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click the eco-friendly button below', Wrapper: Heading },
    ],
    it: [
      { text: 'Il CV provò a scaricarsi ma il server era alimentato a carbone.', Wrapper: BaseText },
      { ms: 1000 },
      { text: "Per un'alternativa più verde:", Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca il pulsante eco-friendly qui sotto', Wrapper: Heading },
    ],
  },
  healthcare: {
    en: [
      { text: 'The CV download needed insurance approval first.', Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Universal download available below', Wrapper: Heading },
    ],
    it: [
      { text: "Il download del CV richiedeva prima l'approvazione dell'assicurazione.", Wrapper: BaseText },
      { ms: 1200 },
      { text: 'Download universale disponibile qui sotto', Wrapper: Heading },
    ],
  },
  twitter: {
    en: [
      { text: 'The CV tried to download on X but got rate limited.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'No subscription required:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Free download below', Wrapper: Heading },
    ],
    it: [
      { text: 'Il CV provò a scaricarsi su X ma ha raggiunto il limite.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Nessun abbonamento richiesto:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Download gratuito qui sotto', Wrapper: Heading },
    ],
  },
  epstein: {
    en: [
      { text: 'The CV download list was sealed by court order.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Declassified version available:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Click below to download', Wrapper: Heading },
    ],
    it: [
      { text: 'La lista dei download del CV è stata sigillata per ordine del tribunale.', Wrapper: BaseText },
      { ms: 1000 },
      { text: 'Versione declassificata disponibile:', Wrapper: BaseText },
      { ms: 600 },
      { text: 'Clicca sotto per scaricare', Wrapper: Heading },
    ],
  },
};

const storyKeys = Object.keys(cvDownloadStories);
const randomStory = () => cvDownloadStories[storyKeys[Math.floor(Math.random() * storyKeys.length)]];

export const DownloadCVView = () => {
  const language = useGlobalStore((store) => store.settings.language);
  const [story] = React.useState(randomStory);

  const theme = useTheme();
  return (
    <Root>
      <Center>
        <ErrorView1
          mt={48}
          message={(
            <TextGenerateEffect
              words={story[language]}
            />
          )}
          cta={{
            node: (
              <ButtonsWrapper>
                <ButtonWrapper>
                  <HoverBorderGradient
                    containerClassName='rounded-full'
                    as='button'
                    className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2'
                    onClick={downloadCVProgrammatically}
                    style={{ cursor: 'pointer' }}
                  >
                    <IconFileDownload color={theme.v0.col.success.hex} />
                    <span>Download CV</span>
                  </HoverBorderGradient>
                </ButtonWrapper>
                <ButtonWrapper>
                  <HoverBorderGradient
                    containerClassName='rounded-full'
                    as='button'
                    className='dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2'
                    onClick={() => navigateTo(homeChatRoute)}
                    style={{ cursor: 'pointer' }}
                  >
                    <IconArrowLeft color={theme.v0.col.warning.hex} />
                    <span>Home</span>
                  </HoverBorderGradient>
                </ButtonWrapper>
              </ButtonsWrapper>
            ),
          }}
        />
      </Center>
    </Root>
  );
};

const Root = styled.div`
  height: 75dvh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  max-width: 500px;
  margin-top: 24px;
  @media (max-width: 769px) {
    max-width: 70vw;
  }
  
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ButtonsWrapper = styled.div`
  margin-top: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  margin-top: 12px;
  :active {
    transform: scale(0.90);
  }
`;
